import { controlSponsorApi } from './axios';
import { useQuery } from '@tanstack/react-query';
import { ValidateCurrentToken, LogoutSystem } from '../../helpers';
import {
  ServiceResponseI,
  DataI,
  ControlSponsorI,
  GetControlSponsorParamsI,
} from '@interfaces';
import { AxiosError } from 'axios';

const getControlSponsors = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetControlSponsorParamsI): Promise<ServiceResponseI<DataI<ControlSponsorI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const params = new URLSearchParams();
    params.append('page',    (pagination.pageIndex + 1).toString());
    params.append('limit',   pagination.pageSize.toString());
    params.append('search',  globalFilter ?? '');
    params.append('sorting', JSON.stringify(sorting ?? []));
    if (clientId)             params.append('clientId', clientId.toString());
    if (status !== undefined) params.append('status',   String(status));

    const { data } = await controlSponsorApi.get<ServiceResponseI<DataI<ControlSponsorI>>>('', {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllControlSponsors = async (
): Promise<ServiceResponseI<ControlSponsorI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await controlSponsorApi.get<ServiceResponseI<ControlSponsorI[]>>(
      '/all-no-pagination',
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlSponsorById = async (
  id: string,
): Promise<ServiceResponseI<ControlSponsorI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await controlSponsorApi.get<ServiceResponseI<ControlSponsorI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlSponsorPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await controlSponsorApi.post<ServiceResponseI<string>>(
      '', payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlSponsorPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await controlSponsorApi.put<ServiceResponseI<string>>(
      `/${id}`, payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlSponsorDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await controlSponsorApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useControlSponsors = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetControlSponsorParamsI) => {
  const controlSponsorsQuery = useQuery(
    ['control-sponsors', { pagination, sorting, globalFilter, clientId, status }],
    () => getControlSponsors({ pagination, sorting, globalFilter, clientId, status }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { controlSponsorsQuery };
};